import type { ColDef } from "ag-grid-community";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { mutationResponse } from "@/utils/trpc";
import type { Tube } from "../../types";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TableFilterRow } from "../TableFilterRow";
import { ArchivedBadge, isTubeArchived } from "./ArchivedBadge";

type TubesTableProps = {
  tubes: Tube[] | undefined;
  showArchived: boolean;
  onShowArchivedChange: (showArchived: boolean) => void;
};

export function TubesTable({
  tubes,
  showArchived,
  onShowArchivedChange,
}: TubesTableProps) {
  const restoreTube = trpc.admin.tube.restore.useMutation();
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const columnDefs = useMemo((): ColDef<Tube>[] => {
    const columns: ColDef<Tube>[] = [
      {
        field: "name",
        headerName: "Container",
        cellRenderer: (params) => (
          <div className="flex items-center gap-2">
            <Link
              to="/admin/tubes/$id/edit"
              params={{ id: params.data?.id ?? 0 }}
            >
              {params.data?.name}
            </Link>
            {params.data && isTubeArchived(params.data) ? (
              <ArchivedBadge />
            ) : null}
          </div>
        ),
      },
      {
        field: "fieldSet.name",
        headerName: "Field set",
        cellRenderer: (params) => (
          <span
            className={
              params.data && isTubeArchived(params.data)
                ? "text-muted-foreground"
                : undefined
            }
          >
            {params.data?.fieldSet?.name}
          </span>
        ),
      },
    ];

    if (showArchived) {
      columns.push({
        colId: "actions",
        headerName: "",
        sortable: false,
        filter: false,
        width: 120,
        cellRenderer: (params) =>
          params.data && isTubeArchived(params.data) ? (
            <ConfirmPrompt
              title="Restore container"
              message="Are you sure you want to restore this container? It will appear in accessioning and menus again."
              loading={restoringId === params.data.id && restoreTube.isPending}
              onConfirm={() => {
                setRestoringId(params.data?.id ?? null);
                restoreTube.mutate(
                  { id: params.data?.id ?? 0 },
                  mutationResponse({
                    errorTitle: "Error restoring container",
                    successMessage: "Container restored",
                    onSuccess() {
                      setRestoringId(null);
                    },
                    onError() {
                      setRestoringId(null);
                    },
                  }),
                );
              }}
            >
              <Button variant="outline" size="sm">
                Restore
              </Button>
            </ConfirmPrompt>
          ) : null,
      });
    }

    return columns;
  }, [restoreTube, restoringId, showArchived]);

  const { options, filterTextProps } = usePersistedAdminGridOptions(
    "tubes",
    tubes,
    columnDefs,
    { sideBar: undefined },
  );

  return (
    <>
      <TableFilterRow inputProps={filterTextProps}>
        <div className="flex items-center gap-4">
          <Switch
            aria-label="Show archived containers"
            isSelected={showArchived}
            onChange={onShowArchivedChange}
          >
            Show archived
          </Switch>
          <Link to="/admin/tubes/new" asButton>
            <PlusIcon data-slot="icon" />
            Add container
          </Link>
        </div>
      </TableFilterRow>
      <AdminGrid {...options} />
    </>
  );
}
